import { clsx } from 'clsx';
import { CaremoryMark } from './icons/caremory-mark';

const RING_SIZE = {
  sm: 'h-10 w-10',
  md: 'h-14 w-14',
  lg: 'h-20 w-20',
} as const;

const MARK_SIZE = {
  sm: 'h-5 w-5',
  md: 'h-7 w-7',
  lg: 'h-10 w-10',
} as const;

type Size = keyof typeof RING_SIZE;

interface CaremoryAiLoaderProps {
  label?: string;
  size?: Size;
  className?: string;
}

export function CaremoryAiLoader({
  label = 'Caremory is reading your prescription…',
  size = 'md',
  className,
}: CaremoryAiLoaderProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx('flex flex-col items-center gap-3', className)}
    >
      <span
        aria-hidden="true"
        className={clsx(
          'relative flex items-center justify-center rounded-full bg-accent-soft',
          RING_SIZE[size],
        )}
      >
        {/*
         * Halo arc — spinning single-colour arc ring (design system: logo-halo).
         * Border is 20%-opacity brand on all sides; border-top is full brand.
         * prefers-reduced-motion: motion-safe collapses the spin.
         */}
        <span
          aria-hidden="true"
          className="absolute rounded-full motion-safe:animate-caremory-halo"
          style={{
            inset: '-10%',
            border: '2px solid rgba(255, 90, 95, 0.22)',
            borderTopColor: '#FF5A5F',
          }}
        />
        {/*
         * Morph — squash, stretch, twist (design system: logo-morph).
         * transform-origin 50% 55% = heart centre; transform-box fill-box
         * makes that origin relative to the SVG path bounding box, not viewport.
         */}
        <CaremoryMark
          variant="icon"
          title=""
          className={clsx(
            MARK_SIZE[size],
            'motion-safe:animate-caremory-morph',
            '[transform-origin:50%_55%] [transform-box:fill-box]',
          )}
        />
      </span>
      <p className="text-sm text-ink-muted text-center max-w-[260px] leading-snug">
        {label}
      </p>
    </div>
  );
}
